import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './Register.css';
import Header from '../../components/header/Header';
import Footer from '../../components/footer/Footer';
import LoginModal from '../../components/loginSection/LoginModal';
import { accountService } from '../../services';
import { setPendingOnboarding } from '../../utils/onboardingState';

const USERNAME_PATTERN = /^[a-z](?:[a-z0-9._-]{1,28}[a-z0-9])?$/;
const USERNAME_REQUIREMENTS = 'Use 3-30 lowercase characters, start with a letter, and only use letters, numbers, dots, hyphens, or underscores.';

const RegisterSection = () => {
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        countryCode: '+44',
        mobileNumber: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        dobDay: '',        
        dobMonth: '',     
        dobYear: '',      
        invitationToken: ''
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isResendingOtp, setIsResendingOtp] = useState(false);
    const [otp, setOtp] = useState('');
    const [registrationComplete, setRegistrationComplete] = useState(false);
    const [registeredMobileNumber, setRegisteredMobileNumber] = useState('');
    const [registeredUserId, setRegisteredUserId] = useState(null);

    const location = useLocation();

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const token = searchParams.get('token');
        if (token) {
            setFormData(prevState => ({ ...prevState, invitationToken: token }));
        }
    }, [location]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const openLoginModal = () => setIsLoginModalOpen(true);
    const closeLoginModal = () => setIsLoginModalOpen(false);

    const isValidEmail = (email) => {
        const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
        return regex.test(email);
    };

    const formatMobileNumber = (countryCode, number) => {
        const cleanNumber = number.replace(/\D/g, '');
        const numberWithoutLeadingZero = cleanNumber.replace(/^0+/, '');
        return `${countryCode}${numberWithoutLeadingZero}`;
    };

    const isValidMobileNumber = (number) => {
        const ukPattern = /^(\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3}$/;
        const usPattern = /^(\+1\s?(\d{3}|\(\d{3}\))[-.\s]?)?\d{3}[-.\s]?\d{4}$/;
        const euPattern = /^\+?(\d{1,4}[-.\s]?)?\(?\d{2,4}\)?([-.\s]?\d{3,4}){2,3}$/;
        return ukPattern.test(number) || usPattern.test(number) || euPattern.test(number);
    };

    const isValidUsername = (username) => USERNAME_PATTERN.test(username.trim().toLowerCase());

    const handleContinueToDetails = () => {
        setError('');

        if (!formData.username.trim()) {
            setError('Please choose a username.');
            return;
        }

        if (!isValidUsername(formData.username)) {
            setError(USERNAME_REQUIREMENTS);
            return;
        }

        if (!isValidEmail(formData.email)) {
            setError('Please enter a valid email address.');
            return;
        }

        if (!formData.password) {
            setError('Please create a password.');
            return;
        }

        if (formData.password.length < 8) {
            setError('Please use a password with at least 8 characters.');
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError("Passwords don't match");
            return;
        }

        setStep(2);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (step === 3) return;

        setIsLoading(true);
        setError('');
        setSuccess('');

        if (!isValidUsername(formData.username)) {
            setError(USERNAME_REQUIREMENTS);
            setIsLoading(false);
            return;
        }
        
        if (!isValidEmail(formData.email)) {
            setError("Please enter a valid email address.");
            setIsLoading(false);
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            setError("Passwords don't match");
            setIsLoading(false);
            return;
        }

        const fullMobileNumber = formatMobileNumber(formData.countryCode, formData.mobileNumber);
        if (!isValidMobileNumber(fullMobileNumber)) {
            setError('Please enter a valid mobile number. This is required for OTP verification.');
            setIsLoading(false);
            return;
        }

        const dateOfBirth = formData.dobYear && formData.dobMonth && formData.dobDay
            ? `${formData.dobYear}-${formData.dobMonth.padStart(2, '0')}-${formData.dobDay.padStart(2, '0')}`
            : null;

        const registrationData = {
            username: formData.username.trim().toLowerCase(),
            email: formData.email,
            password: formData.password,
            mobileNumber: fullMobileNumber,
            firstName: formData.firstName,
            lastName: formData.lastName,
            dateOfBirth: dateOfBirth
        };

        if (formData.invitationToken) {
            registrationData.invitationToken = formData.invitationToken;
        }

        try {
            const data = await accountService.register(registrationData);
            setSuccess('Registration initiated. Please check your phone for an OTP.');
            setRegisteredMobileNumber(fullMobileNumber);
            setRegisteredUserId(data.userId ?? null);
            setStep(3);
        } catch (err) {
            const message = err.message || "An unexpected error occurred. Please try again.";
            const normalized = message.toLowerCase();

            if (registeredMobileNumber && normalized.includes('username is taken')) {
                setError(`Your registration is already in progress. Enter the OTP sent to ${registeredMobileNumber}, or resend the code.`);
                setStep(3);
            } else {
                setError(message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleOtpSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError('');

        try {
            await accountService.verifyOtp(registeredMobileNumber, otp, registeredUserId, 'same-origin');
            setPendingOnboarding({
                source: 'registration',
                username: formData.username,
                firstName: formData.firstName
            });
            setSuccess('Registration successful! You can now log in.');
            setStep(4);
            setRegistrationComplete(true);
        } catch (err) {
            setError(err.message || "An unexpected error occurred. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (!registeredMobileNumber) {
            setError('We could not find the number to resend your verification code.');
            return;
        }

        setIsResendingOtp(true);
        setError('');

        try {
            await accountService.sendOtp(registeredMobileNumber, registeredUserId, 'same-origin');
            setSuccess('A new verification code has been sent to your phone.');
        } catch (err) {
            setError(err.message || 'Failed to resend verification code.');
        } finally {
            setIsResendingOtp(false);
        }
    };

    const renderDateOfBirthDropdowns = () => {
        const days = Array.from({ length: 31 }, (_, i) => i + 1);
        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        const currentYear = new Date().getFullYear();
        const years = Array.from({ length: 100 }, (_, i) => currentYear - i);

        return (
            <div className="form-field">
                <label className="form-label">Date of Birth (Optional)</label>
                <div className="dob-grid">
                    <select
                        name="dobDay"
                        className="form-select"
                        value={formData.dobDay}
                        onChange={handleChange}
                    >
                        <option value="">Day</option>
                        {days.map(day => (
                            <option key={day} value={day}>{day}</option>
                        ))}
                    </select>
                    <select
                        name="dobMonth"
                        className="form-select"
                        value={formData.dobMonth}
                        onChange={handleChange}
                    >
                        <option value="">Month</option>
                        {months.map((month, index) => (
                            <option key={month} value={index + 1}>{month}</option>
                        ))}
                    </select>
                    <select
                        name="dobYear"
                        className="form-select"
                        value={formData.dobYear}
                        onChange={handleChange}
                    >
                        <option value="">Year</option>
                        {years.map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                </div>
            </div>
        );
    };

    const renderStep1 = () => (
        <>
            <div className="form-field">
                <label htmlFor="username" className="form-label">
                    Username <span className="required">*</span>
                </label>
                <input
                    type="text"
                    id="username"
                    name="username"
                    placeholder="Choose a username"
                    className="form-input"
                    value={formData.username}
                    onChange={handleChange}
                    required
                />
                <small className="form-helper-text">{USERNAME_REQUIREMENTS}</small>
            </div>
            <div className="form-field">
                <label htmlFor="email" className="form-label">
                    Email <span className="required">*</span>
                </label>
                <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter your email"
                    className="form-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                />
            </div>
            <div className="form-field">
                <label htmlFor="password" className="form-label">
                    Password <span className="required">*</span>
                </label>
                <input
                    type="password"
                    id="password"
                    name="password"
                    placeholder="Create a password"
                    className="form-input"
                    value={formData.password}
                    onChange={handleChange}
                    required
                />
            </div>
            <div className="form-field">
                <label htmlFor="confirmPassword" className="form-label">
                    Confirm Password <span className="required">*</span>
                </label>
                <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    placeholder="Confirm your password"
                    className="form-input"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                />
            </div>
            <div className="form-actions">
                <button className="btn-primary" type="button" onClick={handleContinueToDetails} disabled={isLoading}>
                    Continue
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                        <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                </button>
            </div>
        </>
    );

    const renderStep2 = () => (
        <>
            <div className="form-row">
                <div className="form-field form-field-small">
                    <label htmlFor="countryCode" className="form-label">
                        Code <span className="required">*</span>
                    </label>
                    <select
                        id="countryCode"
                        name="countryCode"
                        className="form-select"
                        value={formData.countryCode}
                        onChange={handleChange}
                        required
                    >
                        <option value="+44">+44</option>
                        <option value="+1">+1</option>
                        <option value="+33">+33</option>
                        <option value="+49">+49</option>
                    </select>
                </div>
                <div className="form-field form-field-grow">
                    <label htmlFor="mobileNumber" className="form-label">
                        Mobile Number <span className="required">*</span>
                    </label>
                    <input
                        type="tel"
                        id="mobileNumber"
                        name="mobileNumber"
                        placeholder="7911123456"
                        className="form-input"
                        value={formData.mobileNumber}
                        onChange={handleChange}
                        required
                    />
                </div>
            </div>
            <div className="form-row">
                <div className="form-field">
                    <label htmlFor="firstName" className="form-label">First Name</label>
                    <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        placeholder="First name"
                        className="form-input"
                        value={formData.firstName}
                        onChange={handleChange}
                    />
                </div>
                <div className="form-field">
                    <label htmlFor="lastName" className="form-label">Last Name</label>
                    <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        placeholder="Last name"
                        className="form-input"
                        value={formData.lastName}
                        onChange={handleChange}
                    />
                </div>
            </div>
            {renderDateOfBirthDropdowns()}
            <div className="form-actions form-actions-split">
                <button className="btn-secondary" type="button" onClick={() => setStep(1)} disabled={isLoading}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                    Back
                </button>
                <button className="btn-primary" type="submit" disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <svg className="spinner" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" fill="none" strokeDasharray="31.4 31.4" />
                            </svg>
                            Registering...
                        </>
                    ) : (
                        <>
                            Create Account
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="5" y1="12" x2="19" y2="12"></line>
                                <polyline points="12 5 19 12 12 19"></polyline>
                            </svg>
                        </>
                    )}
                </button>
            </div>
        </>
    );

    const renderStep3 = () => (
        <div className="otp-container">
            <div className="otp-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
            </div>
            <h3>Verify Your Phone</h3>
            <p>We've sent a verification code to <strong>{registeredMobileNumber}</strong>. Enter it below to complete registration.</p>
            <div className="form-field">
                <input
                    type="text"
                    id="otp"
                    name="otp"
                    placeholder="Enter 6-digit code"
                    className="form-input otp-input"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    maxLength="6"
                    required
                />
            </div>
            <button className="btn-primary btn-full" type="button" onClick={handleOtpSubmit} disabled={isLoading}>
                {isLoading ? 'Verifying...' : 'Verify & Complete'}
            </button>
            <div className="form-actions form-actions-split otp-actions">
                <button className="btn-secondary" type="button" onClick={() => { setError(''); setSuccess(''); setStep(2); }} disabled={isLoading || isResendingOtp}>
                    Edit Number
                </button>
                <button className="btn-secondary" type="button" onClick={handleResendOtp} disabled={isLoading || isResendingOtp}>
                    {isResendingOtp ? 'Sending...' : 'Resend Code'}
                </button>
            </div>
        </div>
    );

    const renderStep4 = () => (
        <div className="success-container">
            <div className="success-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
            </div>
            <h3>Welcome to ShootConnect!</h3>
            <p>Your account has been created successfully. Sign in now and we’ll guide you through the first setup steps on your dashboard.</p>
            <div className="success-checklist">
                <div className="success-checklist-item">Choose your first venues</div>
                <div className="success-checklist-item">Create or join your first event</div>
                <div className="success-checklist-item">Add equipment and start tracking</div>
            </div>
            <button className="btn-primary btn-full" onClick={openLoginModal}>
                Sign In And Finish Setup
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                </svg>
            </button>
        </div>
    );

    return (
        <div className="register-page">
            <Header openLoginModal={openLoginModal} />
            
            <section className="register-section">
                <div className="register-bg"></div>
                <div className="container">
                    <div className="register-grid">
                        {/* Left Side - Info */}
                        <div className="register-info">
                            <span className="register-badge">Join Free</span>
                            <h1 className="register-title">
                                Start Your<br />
                                <span>Shooting Journey</span>
                            </h1>
                            <p className="register-subtitle">
                                Create your free account and unlock powerful tools to track scores, 
                                find clubs, and connect with the shooting community.
                            </p>
                            
                            <div className="register-features">
                                <div className="register-feature">
                                    <div className="feature-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <circle cx="12" cy="12" r="6"></circle>
                                            <circle cx="12" cy="12" r="2"></circle>
                                        </svg>
                                    </div>
                                    <div>
                                        <h4>Track Your Scores</h4>
                                        <p>Record clay, game, and rifle performance</p>
                                    </div>
                                </div>
                                <div className="register-feature">
                                    <div className="feature-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                                            <circle cx="12" cy="10" r="3"></circle>
                                        </svg>
                                    </div>
                                    <div>
                                        <h4>Find Venues</h4>
                                        <p>Discover clubs, ranges, and shoots near you</p>
                                    </div>
                                </div>
                                <div className="register-feature">
                                    <div className="feature-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                            <line x1="16" y1="2" x2="16" y2="6"></line>
                                            <line x1="8" y1="2" x2="8" y2="6"></line>
                                            <line x1="3" y1="10" x2="21" y2="10"></line>
                                        </svg>
                                    </div>
                                    <div>
                                        <h4>Book Events</h4>
                                        <p>Find and register for competitions</p>
                                    </div>
                                </div>
                                <div className="register-feature">
                                    <div className="feature-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <h4>Manage Equipment</h4>
                                        <p>Track your guns, cartridges, and gear</p>
                                    </div>
                                </div>
                                <div className="register-feature">
                                    <div className="feature-icon">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                            <circle cx="9" cy="7" r="4"></circle>
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <h4>Connect</h4>
                                        <p>Join the shooting community</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        {/* Right Side - Form */}
                        <div className="register-form-container">
                            <div className="register-form-card">
                                {step < 4 && (
                                    <div className="form-header">
                                        <h2>Create Account</h2>
                                        <div className="step-indicator">
                                            <div className={`step ${step >= 1 ? 'active' : ''}`}>1</div>
                                            <div className="step-line"></div>
                                            <div className={`step ${step >= 2 ? 'active' : ''}`}>2</div>
                                            <div className="step-line"></div>
                                            <div className={`step ${step >= 3 ? 'active' : ''}`}>3</div>
                                        </div>
                                    </div>
                                )}

                                <form className="register-form" onSubmit={(e) => {
                                    e.preventDefault();
                                    if (step === 3) {
                                        handleOtpSubmit(e);
                                    } else {
                                        handleSubmit(e);
                                    }
                                }}>
                                    {step === 1 && renderStep1()}
                                    {step === 2 && renderStep2()}
                                    {step === 3 && renderStep3()}
                                    {step === 4 && renderStep4()}
                                </form>

                                {error && (
                                    <div className="form-error">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <line x1="12" y1="8" x2="12" y2="12"></line>
                                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                        </svg>
                                        {error}
                                    </div>
                                )}

                                {success && step < 4 && (
                                    <div className="form-success">
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                                            <polyline points="22 4 12 14.01 9 11.01"></polyline>
                                        </svg>
                                        {success}
                                    </div>
                                )}

                                {step < 3 && (
                                    <div className="form-footer">
                                        Already have an account?{' '}
                                        <button type="button" onClick={openLoginModal} className="login-link">
                                            Sign in
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <LoginModal
                isOpen={isLoginModalOpen}
                onClose={closeLoginModal}
                registrationComplete={registrationComplete}
                username={formData.username}
                onboardingIntent="registration"
            />
            <Footer />
        </div>
    );
};

export default RegisterSection;
